import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Map, Calendar, Wrench, CheckCircle2, Send } from 'lucide-react';
export function InstallationDemos() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    setTimeout(() => {
      setFormStatus('success');
    }, 1500);
  };
  return <div className="bg-white min-h-screen" data-id="element-481">
      {/* Hero */}
      <section className="bg-yarbo-charcoal text-white py-16 lg:py-24" data-id="element-482">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-483">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl font-extrabold mb-6" data-id="element-484">
            Yarbo Demos & Setup Guidance in Michigan
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-485">
            See Yarbo in action before you buy, and get expert local advice on
            setting up your property for autonomous yard care.
          </motion.p>
        </div>
      </section>

      <section className="py-16 lg:py-24" data-id="element-486">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-487">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-488">
            {/* Info Side */}
            <div data-id="element-489">
              <h2 className="text-3xl font-bold text-yarbo-dark mb-8" data-id="element-490">
                Local Support from Merem Robotics
              </h2>

              <div className="space-y-10" data-id="element-491">
                <div className="flex" data-id="element-492">
                  <div className="flex-shrink-0 mt-1" data-id="element-493">
                    <div className="w-12 h-12 bg-yarbo-yellow/20 rounded-xl flex items-center justify-center" data-id="element-494">
                      <Calendar className="w-6 h-6 text-yarbo-dark" data-id="element-495" />
                    </div>
                  </div>
                  <div className="ml-6" data-id="element-496">
                    <h3 className="text-xl font-bold text-yarbo-dark mb-2" data-id="element-497">
                      Schedule a Demo
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-498">
                      Want to see how Yarbo handles Michigan terrain? Ask about
                      our local demo availability. We can show you how the RTK
                      navigation works without perimeter wires and demonstrate
                      the module swapping process.
                    </p>
                  </div>
                </div>

                <div className="flex" data-id="element-499">
                  <div className="flex-shrink-0 mt-1" data-id="element-500">
                    <div className="w-12 h-12 bg-yarbo-yellow/20 rounded-xl flex items-center justify-center" data-id="element-501">
                      <Map className="w-6 h-6 text-yarbo-dark" data-id="element-502" />
                    </div>
                  </div>
                  <div className="ml-6" data-id="element-503">
                    <h3 className="text-xl font-bold text-yarbo-dark mb-2" data-id="element-504">
                      Property Assessment
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-505">
                      Every yard is different. We help you evaluate your
                      property's layout, identify the best location for the RTK
                      antenna and docking station, and ensure Yarbo can navigate
                      your specific slopes and obstacles.
                    </p>
                  </div>
                </div>

                <div className="flex" data-id="element-506">
                  <div className="flex-shrink-0 mt-1" data-id="element-507">
                    <div className="w-12 h-12 bg-yarbo-yellow/20 rounded-xl flex items-center justify-center" data-id="element-508">
                      <Wrench className="w-6 h-6 text-yarbo-dark" data-id="element-509" />
                    </div>
                  </div>
                  <div className="ml-6" data-id="element-510">
                    <h3 className="text-xl font-bold text-yarbo-dark mb-2" data-id="element-511">
                      Setup Guidance
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-512">
                      While Yarbo is designed for user-friendly setup via the
                      smartphone app, we provide expert guidance to ensure your
                      virtual boundaries and cleaning zones are optimized for
                      efficiency and safety. Ask about our local setup options.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 bg-gray-50 p-6 rounded-xl border border-gray-200" data-id="element-513">
                <h4 className="font-bold text-yarbo-dark mb-3 flex items-center" data-id="element-514">
                  <CheckCircle2 className="w-5 h-5 text-yarbo-green mr-2" data-id="element-515" />
                  Important Note on Installation
                </h4>
                <p className="text-sm text-gray-600" data-id="element-516">
                  Yarbo is a wire-free system, meaning there is no need to bury
                  perimeter wires around your property. Setup primarily involves
                  placing the docking station, mounting the RTK antenna with a
                  clear view of the sky, and mapping your yard using the Yarbo
                  app. We are here to guide you through this process.
                </p>
              </div>
            </div>

            {/* Form Side */}
            <div data-id="element-517">
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 relative overflow-hidden" data-id="element-518">
                <div className="absolute top-0 left-0 w-full h-2 bg-yarbo-yellow" data-id="element-519"></div>

                {formStatus === 'success' ? <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="text-center py-12" data-id="element-520">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-521">
                      <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-522" />
                    </div>
                    <h3 className="text-2xl font-bold text-yarbo-dark mb-2" data-id="element-523">
                      Request Received
                    </h3>
                    <p className="text-gray-600 mb-6" data-id="element-524">
                      We'll contact you shortly to discuss demo and setup
                      options for your property.
                    </p>
                    <button onClick={() => setFormStatus('idle')} className="text-yarbo-dark font-bold hover:underline" data-id="element-525">
                      Submit another request
                    </button>
                  </motion.div> : <>
                    <h3 className="text-2xl font-bold text-yarbo-dark mb-6" data-id="element-526">
                      Request a Demo or Setup Help
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-5" data-id="element-527">
                      <div data-id="element-528">
                        <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-529">
                          Name *
                        </label>
                        <input type="text" required className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-530" />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-531">
                        <div data-id="element-532">
                          <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-533">
                            Email *
                          </label>
                          <input type="email" required className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-534" />
                        </div>
                        <div data-id="element-535">
                          <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-536">
                            Phone
                          </label>
                          <input type="tel" className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-537" />
                        </div>
                      </div>

                      <div data-id="element-538">
                        <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-539">
                          Michigan City/Zip *
                        </label>
                        <input type="text" required className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-540" />
                      </div>

                      <div data-id="element-541">
                        <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-542">
                          I am interested in:
                        </label>
                        <select className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none bg-white" data-id="element-543">
                          <option data-id="element-544">Scheduling a local demo</option>
                          <option data-id="element-545">Help assessing my property</option>
                          <option data-id="element-546">Setup/Installation guidance</option>
                          <option data-id="element-547">General questions</option>
                        </select>
                      </div>

                      <div data-id="element-548">
                        <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-549">
                          Additional Details
                        </label>
                        <textarea rows={3} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none resize-none" placeholder="Tell us about your yard size, terrain, etc..." data-id="element-550"></textarea>
                      </div>

                      <button type="submit" disabled={formStatus === 'submitting'} className="btn-primary w-full py-3 mt-2 flex justify-center items-center" data-id="element-551">
                        {formStatus === 'submitting' ? 'Sending...' : 'Submit Request'}
                      </button>
                    </form>
                  </>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}